import React, { CSSProperties, useEffect, useState } from 'react'
import { TableCell, TableRow, styled, Collapse, Typography, TableBody, Table } from '@mui/material'
import { Draggable, DraggableProvided } from 'react-beautiful-dnd'
import { MthCheckbox } from '@mth/components/MthCheckbox'
import { MthColor } from '@mth/enums'
import { convertWidth } from '@mth/utils/mthtable/mthtable.util'
import { MthTableField, MthTableRowItem, MthTableRowProps } from './types'

const StyledTableRow = styled(TableRow)(({}) => ({
  '& .MuiTableCell-root': {
    backgroundColor: MthColor.WHITE,
  },
  '&:nth-of-type(2n+1) .MuiTableCell-root': {
    backgroundColor: '#FAFAFA',
    '&:first-of-type': {
      borderTopLeftRadius: '8px',
      borderBottomLeftRadius: '8px',
    },
    '&:last-child': {
      borderTopRightRadius: '8px',
      borderBottomRightRadius: '8px',
    },
  },
  td: {
    padding: '2px 4px 2px 0',
  },
  '.cell-item': {
    textAlign: 'left',
  },
  '.cell-item.border-l': {
    borderLeft: '1px solid black',
    minHeight: '17px',
  },
}))

const getStyle = (key: string, value: string): CSSProperties => {
  switch (key) {
    case 'status':
      return value === 'Not Submitted' ? { color: MthColor.BLACK } : { color: MthColor.MTHBLUE }
    default:
      return {}
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
const MthTableRow = <T extends unknown>({
  tableWidth,
  fields,
  index,
  item,
  selectable = false,
  isDraggable,
  size,
  checkBoxColor,
  handleToggleCheck,
  isTableCellBorder = true,
  noCellItemPadding,
}: MthTableRowProps<T>): React.ReactElement => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const renderDraggableTableRow = (isSelectable: boolean, provided: DraggableProvided, item: MthTableRowItem<T>) => {
    return (
      <TableRow
        ref={provided.innerRef}
        {...provided.draggableProps}
        sx={{
          '&:nth-of-type(2n+0) td': {
            backgroundColor: 'white !important',
          },
          ...item.sx,
        }}
      >
        <TableCell colSpan={12}>
          <Table>
            <TableBody>{renderGeneralTableRow(isSelectable, provided, item)}</TableBody>
          </Table>
        </TableCell>
      </TableRow>
    )
  }

  const handleClick = (field: MthTableField<T>) => {
    if (field.onClick) field.onClick(index)
  }

  const renderGeneralTableRow = (
    isSelectable: boolean,
    provided: DraggableProvided | undefined,
    item: MthTableRowItem<T>,
  ) => {
    return (
      <>
        <StyledTableRow className={isExpanded ? 'expanded' : ''} sx={item.sx}>
          {isSelectable && (
            <TableCell className='checkWrap'>
              <MthCheckbox
                color={item.isSelectedPartial ? 'primary' : checkBoxColor}
                size={size}
                indeterminate={item.isSelectedPartial}
                checked={item.isSelected || false}
                onMouseDown={(event) => handleToggleCheck(item, index, event)}
                disabled={item.selectable === false}
              />
            </TableCell>
          )}
          {fields.map((field, indexCol) => (
            <TableCell
              data-testid={`tableCell-${index}-${field.key}`}
              key={indexCol}
              width={convertWidth(field.width || 0, tableWidth)}
              sx={getStyle(field.key, item.columns[field.key] as string)}
              onClick={() => handleClick(field)}
            >
              <div
                className={
                  indexCol > 0 && indexCol + 1 !== fields.length && isTableCellBorder
                    ? 'border-l cell-item'
                    : 'cell-item'
                }
                style={{ paddingLeft: noCellItemPadding ? '0 !important' : '14px' }}
              >
                {field.formatter ? field.formatter(item, provided?.dragHandleProps) : item.columns[field.key]}
              </div>
            </TableCell>
          ))}
        </StyledTableRow>
        {Boolean(item.expandNode) && (
          <>
            <TableRow />
            <TableRow>
              <TableCell sx={{ p: 0 }} colSpan={12}>
                <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                  <Typography variant='h6' gutterBottom component='div' border={'none'}>
                    {item.expandNode}
                  </Typography>
                </Collapse>
              </TableCell>
            </TableRow>
          </>
        )}
      </>
    )
  }

  useEffect(() => {
    item.toggleExpand = () => {
      setIsExpanded((pre) => !pre)
    }
  }, [item])

  return (
    <>
      {isDraggable ? (
        <Draggable key={index.toString()} draggableId={index.toString()} index={index} isDragDisabled={!isDraggable}>
          {(provided: DraggableProvided) => renderDraggableTableRow(selectable, provided, item)}
        </Draggable>
      ) : (
        renderGeneralTableRow(selectable, undefined, item)
      )}
    </>
  )
}

export default MthTableRow
